import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { loginAction } from "../../../store/slices/auth/authSliceActions";
import { clearError } from "../../../store/slices/auth/authSlice";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { isAuthenticated, apiStatus, error } = useAppSelector(
    (state) => state.auth
  );

  const isLoading = apiStatus.login === API_STATUS.STARTED;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin/overview");
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      return;
    }

    dispatch(loginAction({ email, password }));
  };

  const containerStyle = {
    minHeight: "calc(100vh - 80px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.extraLightGray,
    padding: "1rem",
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "3rem",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: "400px",
  };

  const titleStyle = {
    fontSize: "2rem",
    fontWeight: "700",
    color: colors.darkGreen,
    textAlign: "center",
    marginBottom: "2rem",
  };

  const inputGroupStyle = {
    position: "relative",
    marginBottom: "1.5rem",
  };

  const inputStyle = {
    width: "100%",
    padding: "1rem 3rem 1rem 3rem",
    border: `2px solid ${colors.doubleExtraLightGray}`,
    borderRadius: "8px",
    fontSize: "16px",
    outline: "none",
    transition: "border-color 0.3s ease",
    backgroundColor: colors.white,
  };

  const iconStyle = {
    position: "absolute",
    left: "1rem",
    top: "50%",
    transform: "translateY(-50%)",
    color: colors.darkGray,
    width: "20px",
    height: "20px",
  };

  const eyeIconStyle = {
    position: "absolute",
    right: "1rem",
    top: "50%",
    transform: "translateY(-50%)",
    color: colors.darkGray,
    cursor: "pointer",
    width: "20px",
    height: "20px",
  };

  const buttonStyle = {
    width: "100%",
    padding: "1rem",
    backgroundColor: colors.darkGreen,
    color: colors.white,
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    marginTop: "1rem",
  };

  const errorStyle = {
    color: "#dc2626",
    fontSize: "0.875rem",
    textAlign: "center",
    marginTop: "1rem",
    padding: "0.5rem",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px",
  };

  return (
    <div style={containerStyle} className="admin-responsive-container">
      <div style={cardStyle} className="admin-login-card admin-card">
        <h1 style={titleStyle} className="admin-login-title">
          Admin Login
        </h1>

        <form onSubmit={handleLogin}>
          <div style={inputGroupStyle}>
            <Mail style={iconStyle} />
            <input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                ...inputStyle,
                borderColor: email
                  ? colors.darkGreen
                  : colors.doubleExtraLightGray,
              }}
              required
            />
          </div>

          <div style={inputGroupStyle}>
            <Lock style={iconStyle} />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                ...inputStyle,
                borderColor: password
                  ? colors.darkGreen
                  : colors.doubleExtraLightGray,
              }}
              required
            />
            <div
              style={eyeIconStyle}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </div>
          </div>

          <button
            type="submit"
            style={buttonStyle}
            disabled={isLoading}
            onMouseOver={(e) =>
              !isLoading && (e.target.style.backgroundColor = "#0f3c20")
            }
            onMouseOut={(e) =>
              !isLoading && (e.target.style.backgroundColor = colors.darkGreen)
            }
          >
            {isLoading ? "Signing In..." : "Sign In"}
          </button>

          {error && <div style={errorStyle}>{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
